import React from "react";
import { Box } from "@mui/material";
import { useWindowSize } from "@uidotdev/usehooks";

// Template305: Menú de ciudades para guías multi-ciudad (ej. Guía de Japón: Tokio / Kioto / Osaka).
// Muestra N tarjetas grandes, cada una con imagen + nombre de ciudad + subtítulo opcional (ej. "6 días").
// Al hacer clic navega a la página cuyo templateId coincide con el targetTemplateId del item,
// o bien a `pageId` si está definido explícitamente en el item.
const Template305 = ({
	backgroundColor,
	textColor,
	headerBackgroundColor,
	headerTextColor,
	fontFamily,
	page,
	onNavigateToPage,
	pages = [],
}) => {
	const size = useWindowSize();
	const isSmallScreen = size.width <= 600;

	const pageData = page?.data || {};
	const ciudadesRaw = Array.isArray(pageData.ciudades) ? pageData.ciudades : [];

	const findPageByTemplateId = (templateId) => {
		return pages.find(
			(p) => p.templateId === templateId || p.data?.templateId === templateId,
		);
	};
	const getPageIdOrIndex = (targetPage) => {
		if (!targetPage) return null;
		const id = targetPage?.id ?? targetPage?.data?.id;
		if (id != null) return id;
		const idx = pages.indexOf(targetPage);
		return idx >= 0 ? idx : null;
	};

	// Paleta por índice (suave, para el disco detrás del personaje).
	// Se puede sobreescribir por ítem con `ciudad.accentColor`.
	const defaultAccents = [
		{ bg: "#FFE2D6", ring: "#F4845F" }, // coral suave
		{ bg: "#E5F1DC", ring: "#7FB069" }, // verde suave
		{ bg: "#FFF1D1", ring: "#E8A33D" }, // amarillo/ocre
		{ bg: "#E0EEFB", ring: "#5DA9E9" }, // azul suave
		{ bg: "#F0E3F7", ring: "#A774D4" }, // lila
	];

	const ciudades = ciudadesRaw.map((ciudad, idx) => {
		const targetPage = ciudad.targetTemplateId
			? findPageByTemplateId(ciudad.targetTemplateId)
			: null;
		const palette = defaultAccents[idx % defaultAccents.length];
		return {
			...ciudad,
			pageId: ciudad.pageId ?? getPageIdOrIndex(targetPage),
			accentBg: ciudad.accentBg ?? palette.bg,
			accentRing: ciudad.accentRing ?? palette.ring,
		};
	});

	const actualHeaderColor = headerBackgroundColor || "#24221a";
	const headerText = page?.texts?.[0]?.content ?? "Elige tu destino";

	const getCanvasImageUrl = (id) => {
		if (page?.canvasImages && Array.isArray(page.canvasImages)) {
			const found = page.canvasImages.find((img) => img?.id === id);
			if (found?.imageUrl) return found.imageUrl;
		}
		return undefined;
	};
	const logoImage = getCanvasImageUrl("logo");

	const handleNavigate = (pageId) => {
		if (onNavigateToPage && pageId != null) onNavigateToPage(pageId);
	};

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				minHeight: "100%",
				height: "auto",
				width: "100%",
				backgroundColor: backgroundColor || "#f7f7f7",
				fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				position: "relative",
				"& *": {
					fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				},
			}}
		>
			<Box
				data-template-header
				sx={{
					backgroundColor: actualHeaderColor,
					color: headerTextColor || "#fff",
					textAlign: "center",
					padding: "15px 0",
					fontSize: "14px",
					fontWeight: 800,
					position: "sticky",
					top: 0,
					zIndex: 1,
				}}
			>
				{headerText}
				{logoImage && (
					<Box
						component="img"
						src={logoImage}
						alt=""
						sx={{
							width: isSmallScreen ? 66 : 80,
							position: "absolute",
							right: 2,
							top: 18,
							zIndex: 999999,
						}}
					/>
				)}
			</Box>

			<Box
				sx={{
					padding: isSmallScreen ? "16px 12px" : "24px 20px",
					display: "flex",
					flexDirection: "column",
					gap: isSmallScreen ? "14px" : "18px",
				}}
			>
				{ciudades.map((ciudad, index) => {
					const clickable = ciudad.pageId != null;
					const discSize = isSmallScreen ? 76 : 96;
					const imgSize = isSmallScreen ? 92 : 118;
					const discLeft = isSmallScreen ? 14 : 20;
					return (
						<Box
							key={index}
							onClick={() => clickable && handleNavigate(ciudad.pageId)}
							sx={{
								position: "relative",
								backgroundColor: "#fff",
								borderRadius: "20px",
								display: "flex",
								alignItems: "center",
								cursor: clickable ? "pointer" : "default",
								boxShadow: "0 4px 16px rgba(20,20,40,0.06)",
								border: "1px solid rgba(0,0,0,0.04)",
								transition:
									"transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease",
								minHeight: isSmallScreen ? 100 : 124,
								paddingLeft: `${discLeft + discSize + (isSmallScreen ? 12 : 16)}px`,
								paddingRight: isSmallScreen ? "44px" : "52px",
								"& .t305-arrow": {
									transition: "transform 0.25s ease, opacity 0.25s ease",
								},
								"&:hover": clickable
									? {
											transform: "translateY(-3px)",
											boxShadow: "0 10px 24px rgba(20,20,40,0.12)",
											borderColor: "rgba(0,0,0,0.08)",
											"& .t305-arrow": {
												transform: "translate(4px, -50%)",
												opacity: 1,
											},
										}
									: {},
							}}
						>
							{/* Disco de color (fondo del personaje) */}
							<Box
								aria-hidden
								sx={{
									position: "absolute",
									left: `${discLeft}px`,
									top: "50%",
									transform: "translateY(-50%)",
									width: discSize,
									height: discSize,
									borderRadius: "50%",
									backgroundColor: ciudad.accentBg,
									boxShadow: `0 6px 14px rgba(20,20,40,0.08)`,
									zIndex: 1,
								}}
							/>

							{/* Personaje/ilustración (centrada sobre el disco) */}
							{ciudad.image && (
								<Box
									component="img"
									src={ciudad.image}
									alt={ciudad.titulo || ""}
									sx={{
										position: "absolute",
										left: `${discLeft + discSize / 2 - imgSize / 2}px`,
										top: "50%",
										transform: "translateY(-54%)",
										width: imgSize,
										height: "auto",
										objectFit: "contain",
										pointerEvents: "none",
										zIndex: 2,
									}}
								/>
							)}

							{/* Texto */}
							<Box
								sx={{
									flex: 1,
									display: "flex",
									flexDirection: "column",
									justifyContent: "center",
									padding: isSmallScreen ? "12px 4px" : "16px 4px",
									gap: "6px",
									minWidth: 0,
								}}
							>
								<Box
									sx={{
										display: "flex",
										alignItems: "center",
										gap: "10px",
										flexWrap: "wrap",
									}}
								>
									<Box
										sx={{
											fontSize: isSmallScreen ? "19px" : "24px",
											fontWeight: 800,
											color: "#1a1a1a",
											lineHeight: 1.1,
											letterSpacing: "-0.01em",
										}}
									>
										{ciudad.titulo}
									</Box>
									{ciudad.subtitulo && (
										<Box
											sx={{
												display: "inline-flex",
												alignItems: "center",
												fontSize: isSmallScreen ? "11px" : "12px",
												fontWeight: 700,
												color: ciudad.accentRing,
												backgroundColor: ciudad.accentBg,
												borderRadius: "999px",
												padding: isSmallScreen
													? "3px 10px"
													: "4px 12px",
												lineHeight: 1,
												letterSpacing: "0.02em",
											}}
										>
											{ciudad.subtitulo}
										</Box>
									)}
								</Box>
								{ciudad.descripcion && (
									<Box
										sx={{
											fontSize: isSmallScreen ? "12px" : "13px",
											fontWeight: 400,
											color: "#5a5a66",
											lineHeight: 1.45,
											display: "-webkit-box",
											WebkitLineClamp: 2,
											WebkitBoxOrient: "vertical",
											overflow: "hidden",
										}}
									>
										{ciudad.descripcion}
									</Box>
								)}
							</Box>

							{/* Chevron */}
							{clickable && (
								<Box
									className="t305-arrow"
									aria-hidden
									sx={{
										position: "absolute",
										right: isSmallScreen ? 14 : 20,
										top: "50%",
										transform: "translate(0, -50%)",
										width: isSmallScreen ? 26 : 30,
										height: isSmallScreen ? 26 : 30,
										borderRadius: "50%",
										backgroundColor: ciudad.accentBg,
										color: ciudad.accentRing,
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										fontSize: isSmallScreen ? "14px" : "16px",
										fontWeight: 800,
										opacity: 0.85,
										lineHeight: 1,
									}}
								>
									›
								</Box>
							)}
						</Box>
					);
				})}
			</Box>
		</Box>
	);
};

Template305.thumbnail = "/images/template-1.png";

export default Template305;
